import { ReactElement, useEffect, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'
import { useDispatch, useSelector } from 'react-redux'
import { useFormikContext } from 'formik'
import get from 'lodash/get'
import styled from 'styled-components'
import { Button, Grid, P, Wrapper } from '@farewill/ui'
import { GTR } from '@farewill/ui/tokens'

import { Celebration } from 'components/celebration-form-fields'
import { FakePaymentButton } from 'components/fake-payment-button'
import MessageBox from 'components/message-box'
import { PaymentProviderCard } from 'components/payment-provider-card'
import { FuneralBrandId } from 'config/brands'
import ENV from 'config/environment'
import { useVisibilityChange } from 'hooks/useVisibilityChange'
import { formatCurrency, hasDecimals } from 'lib/formatting/currency'
import { Constants } from 'lib/models/constants'
import { FuneralPlan, FuneralPlanAttributes } from 'lib/models/funeral-plan'
import { launchPciPalSession } from 'lib/pci-pal/launch-session'
import { addFuneralPlanCelebration, generatePciPalSession } from 'state/actions'

import {
  CELEBRATION_PURCHASE,
  hasActiveCelebration,
} from './celebration-helpers'
import { CaseDetailsSectionProps } from './types'

/** mirrors CELEBRATION_PAYMENT_NOT_FOUND in server/src/shared/utils/errors.ts */
export const CELEBRATION_PAYMENT_NOT_FOUND = 'celebration_payment_not_found'

/** We process the Stripe webhook event that confirms the payment a few seconds after
 * PCI Pal takes it, so we keep checking for ~20s before calling it a failure. */
const DEFAULT_POLL_INTERVAL_MS = 4000
const DEFAULT_MAX_CONFIRM_ATTEMPTS = 5

/** The plan's own celebrationPurchase, plus the add flow's choices, which
 * aren't saved to the plan until payment is confirmed */
const FIELD_NAMES = {
  CELEBRATION_PURCHASE: 'celebrationPurchase',
  CELEBRATION_TYPE: 'newCelebrationType',
  URN_TYPE: 'newUrnType',
} as const

type FormValues = {
  [FIELD_NAMES.CELEBRATION_TYPE]: string
  [FIELD_NAMES.URN_TYPE]: string
}

type Step =
  'payment' | 'paymentTaken' | 'confirming' | 'success' | 'failed' | 'error'

const StyledButtonRow = styled(Wrapper)`
  display: flex;
  gap: ${GTR.S};
  align-items: center;
  margin-top: ${GTR.M};
`

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const isPaymentNotFound = (error: unknown): boolean =>
  get(error, 'response.status') === 422 &&
  get(error, 'response.data.errors[0].code') === CELEBRATION_PAYMENT_NOT_FOUND

export const CelebrationInterestEditable = ({
  handleSave,
}: CaseDetailsSectionProps): ReactElement | null => {
  const { values } = useFormikContext<FuneralPlanAttributes & FormValues>()
  const dispatch = useDispatch()
  const cache = useQueryClient()
  const constants = cache.getQueryData('constants') as Constants
  const funeralPlan = useSelector(
    (state: { funeralPlanCase: FuneralPlan }) => state.funeralPlanCase
  )
  const [step, setStep] = useState<Step>('payment')
  const [isOpeningPciPal, setIsOpeningPciPal] = useState(false)
  /** Stops multiple PCIPal tabs from opening */
  const isOpeningPciPalRef = useRef(false)

  /** The celebration and urn choices the customer is paying for, kept whilst
   * we wait for payment confirmation and cleared once the celebration is on
   * the plan. Only kept while the user stays on the page. */
  const [pendingChoice, setPendingChoice] = useState<FormValues | null>(null)
  const isConfirming = useRef(false)
  const isTabVisible = useVisibilityChange()

  const { attributes } = funeralPlan
  const packageSlug = attributes.package?.slug as string
  const brandId = attributes.package?.brandId as FuneralBrandId

  const isPlanActive = attributes.status === 'active'

  const getAmountInPence = ({ newCelebrationType, newUrnType }: FormValues) => {
    const celebrationPrice =
      constants?.attributes?.celebrationPrices?.[packageSlug]?.[
        newCelebrationType
      ] ?? 0
    const urnPrice =
      constants?.attributes?.urnPrices?.[
        attributes.package?.brandId as FuneralBrandId
      ]?.[packageSlug]?.[newUrnType]?.celebrationFirstUrnPrice ?? 0
    return (celebrationPrice + urnPrice) * 100
  }

  const openPciPalTab = async (values: FormValues) => {
    if (isOpeningPciPalRef.current) return
    isOpeningPciPalRef.current = true
    setIsOpeningPciPal(true)
    try {
      const response = await dispatch(
        generatePciPalSession({
          referenceId: attributes.leadUuid,
          paymentType: 'in_full',
          amountInPence: getAmountInPence(values),
          product: 'funeral_plan',
        })
      )
      const data = get(response, 'value.data.data')
      launchPciPalSession({
        url: get(data, 'url'),
        accessToken: get(data, 'accessToken'),
        refreshToken: get(data, 'refreshToken'),
      })
      setPendingChoice({
        newCelebrationType: values.newCelebrationType,
        newUrnType: values.newUrnType,
      })
      setStep('paymentTaken')
    } catch {
      // Backstage error handling will surface details to the user
    } finally {
      isOpeningPciPalRef.current = false
      setIsOpeningPciPal(false)
    }
  }

  const confirmPayment = async ({
    newCelebrationType,
    newUrnType,
  }: FormValues) => {
    if (isConfirming.current) return
    isConfirming.current = true
    setStep('confirming')

    /** Try and add the celebration to the plan -- BE will check that there is a payment */
    for (let attempt = 1; attempt <= DEFAULT_MAX_CONFIRM_ATTEMPTS; attempt++) {
      try {
        await dispatch(
          addFuneralPlanCelebration(funeralPlan.id, {
            celebrationType: newCelebrationType,
            urnType: newUrnType,
          })
        )
        setPendingChoice(null)
        setStep('success')
        isConfirming.current = false
        return
      } catch (error) {
        if (!isPaymentNotFound(error)) {
          /** Anything else is shown by the global error handling. The payment
           * may well have gone through, so let the user check again rather
           * than calling the payment failed. */
          setStep('error')
          isConfirming.current = false
          return
        }
        if (attempt < DEFAULT_MAX_CONFIRM_ATTEMPTS)
          await wait(DEFAULT_POLL_INTERVAL_MS)
      }
    }

    setStep('failed')
    isConfirming.current = false
  }

  const hasCelebration = hasActiveCelebration(attributes.items)
  /** The effect below only reacts to the tab being shown again, so it reads
   * everything else from here */
  const latest = useRef({
    pendingChoice,
    isPlanActive,
    step,
    confirmPayment,
  })
  latest.current = {
    pendingChoice,
    isPlanActive,
    step,
    confirmPayment,
  }

  /** Back from the PCI Pal tab: check without waiting to be asked */
  useEffect(() => {
    const { pendingChoice, isPlanActive, step, confirmPayment } = latest.current
    if (
      isTabVisible &&
      isPlanActive &&
      pendingChoice &&
      (step === 'paymentTaken' || step === 'failed' || step === 'error')
    ) {
      confirmPayment(pendingChoice)
    }
  }, [isTabVisible])

  if (step === 'success' && hasCelebration) {
    return (
      <Grid.Item data-testid="celebration-interest">
        <Wrapper padding={[GTR.S, 0]}>
          <MessageBox success>Celebration successfully added.</MessageBox>
        </Wrapper>
      </Grid.Item>
    )
  }

  if (hasActiveCelebration(values.items)) return null

  const handleCelebrationSave = ({
    name,
    value,
  }: {
    name: string
    value: string | null
  }) => {
    if (name !== FIELD_NAMES.CELEBRATION_PURCHASE) return
    /** Start the payment flow afresh whenever the answer changes */
    setStep('payment')
    /** We will only save CELEBRATION_PURCHASE.YES on the BE upon successful payment, so do not send here */
    if (value === CELEBRATION_PURCHASE.YES) return
    setPendingChoice(null)
    handleSave({ name, value })
  }

  const hasChosenCelebration =
    values.celebrationPurchase === CELEBRATION_PURCHASE.YES &&
    !!values.newCelebrationType &&
    !!values.newUrnType

  const renderPaymentStep = (choice: FormValues) => {
    const amountInPence = getAmountInPence(choice)
    const amountToCharge = formatCurrency({
      showPence: hasDecimals(amountInPence / 100),
      value: amountInPence,
      valueInPence: true,
    })

    return (
      <Wrapper data-testid="add-celebration" margin={[GTR.M, 0]}>
        {(step === 'payment' || step === 'paymentTaken') && (
          <>
            <P data-testid="celebration-amount-to-charge" strong>
              You will be charging the customer {amountToCharge}
            </P>
            <PaymentProviderCard
              onPciPalClick={() => openPciPalTab(choice)}
              isLoading={isOpeningPciPal}
            />
            {/* Handy fake payment button for non-prod environments where you don't want to do the PCI Pal rigmarole */}
            {ENV.APP_ENV !== 'production' && (
              <Wrapper margin={['S', 0, 0]}>
                <FakePaymentButton
                  funeralPlanId={funeralPlan.id}
                  paymentType="in_full"
                  amountInPence={amountInPence}
                  onPaymentCreated={() => {
                    /** The fake paid invoice should already exist, so there's nothing to
                     * wait for: try adding the celebration straight away */
                    setPendingChoice({
                      newCelebrationType: choice.newCelebrationType,
                      newUrnType: choice.newUrnType,
                    })
                    confirmPayment(choice)
                  }}
                />
              </Wrapper>
            )}
            {step === 'paymentTaken' && (
              <>
                {/* This is a fallback as coming back to the Backstage tab should 
                auto-trigger the 'confirmation' step, so this is for a weird scenario, 
                e.g. PCI Pal was opened in a window beside Backstage */}
                <P margin={['M', 0, 0]}>
                  Taken payment, but the celebration hasn’t been added yet?
                  Click <strong>I’ve taken payment</strong> to check.
                </P>
                <StyledButtonRow>
                  <Button.Primary
                    type="button"
                    onClick={() => confirmPayment(choice)}
                  >
                    I’ve taken payment
                  </Button.Primary>
                </StyledButtonRow>
              </>
            )}
          </>
        )}

        {step === 'confirming' && (
          <MessageBox>Checking for payment confirmation...</MessageBox>
        )}

        {step === 'failed' && (
          <MessageBox error>
            <P strong margin={0}>
              Payment failed
            </P>
            <P>
              We couldn’t confirm the payment of {amountToCharge} for the
              celebration. The celebration has not been added to the plan.
            </P>
            <P>
              The payment may not have gone through, or it may still be on its
              way. Check again or retry the payment.
            </P>
            <StyledButtonRow>
              <Button.Primary
                type="button"
                loading={isOpeningPciPal}
                onClick={() => openPciPalTab(choice)}
              >
                Retry payment
              </Button.Primary>
              <Button type="button" onClick={() => confirmPayment(choice)}>
                Check again
              </Button>
            </StyledButtonRow>
          </MessageBox>
        )}

        {step === 'error' && (
          <MessageBox error>
            <P strong margin={0}>
              We couldn’t check for the payment
            </P>
            <P>
              Something went wrong while checking for the payment of{' '}
              {amountToCharge}. The celebration has not been added to the plan
              yet, but the payment may have gone through, so check again before
              taking another one.
            </P>
            <StyledButtonRow>
              <Button.Primary
                type="button"
                onClick={() => confirmPayment(choice)}
              >
                Check again
              </Button.Primary>
            </StyledButtonRow>
          </MessageBox>
        )}
      </Wrapper>
    )
  }

  /** Saving another field on the page reinitialises the form, which forgets
   * the unsaved "Yes, add now", so keep showing the payment from the choice */
  const resumeChoice =
    isPlanActive && !hasChosenCelebration ? pendingChoice : null

  return (
    <Grid.Item data-testid="celebration-interest">
      {!isPlanActive && (
        <MessageBox>
          A celebration can only be added to an active plan.
        </MessageBox>
      )}
      <Celebration
        fieldNames={FIELD_NAMES}
        packageSlug={packageSlug}
        brandId={brandId}
        onSave={handleCelebrationSave}
        isPurchaseNowDisabled={!isPlanActive}
        hideDivider
      >
        {hasChosenCelebration && renderPaymentStep(values)}
      </Celebration>

      {resumeChoice && renderPaymentStep(resumeChoice)}
    </Grid.Item>
  )
}
